import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import { useTranslation } from 'react-i18next'

import { ArrowForwardIcon } from '@/components/icons'
import ReservationExpiryChip from '@/components/checkout/order-summary/reservation-expiry-chip'
import CheckoutEndTime from '@/components/checkout/order-summary/end-time'
import { formatCzkAmount } from '@/components/checkout/order-summary/utils'
import ReservationDuration from '@/components/reservation-duration'
import type { CheckoutSummaryProps } from '@/types/reservation-checkout'

export default function CheckoutOrderSummaryMobile({
    garageName,
    owner,
    beginsAtDateLabel,
    beginsAtTimeLabel,
    endsAtDateLabel,
    endsAtTimeLabel,
    reservationDays,
    reservationHours,
    finalPrice,
    originalPrice,
    discountAmount,
    expiredAt,
    minimumStay,
}: CheckoutSummaryProps) {
    const { t, i18n } = useTranslation()
    const hasDiscount = typeof originalPrice === 'number' && typeof discountAmount === 'number' && discountAmount > 0
    const locale = i18n.resolvedLanguage || i18n.language

    return (
        <Box sx={{
            p: 1,
            pb: 0.5,
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                background: 'white',
                borderRadius: 10,
                boxShadow: `var(--joy-shadowRing, 0 0 #000),
                    0.3px 0.8px 1.1px rgba(187 187 187 / 0.11),
                    0.5px 1.3px 1.8px -0.6px rgba(187 187 187 / 0.18),
                    1.1px 2.7px 3.8px -1.2px rgba(187 187 187 / 0.26)`,
                border: '1px solid var(--joy-palette-neutral-100)',
                gap: 1,
            }}>
                <Box>
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start',
                    }}>
                        <Typography level="body-md" sx={{
                            fontWeight: 600,
                            flexGrow: 1,
                        }}>
                            {garageName}
                        </Typography>
                        <ReservationExpiryChip expiredAt={expiredAt} size="sm" />
                    </Box>
                    {owner ? (
                        <Typography level="body-xs" textColor="neutral.500" sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, max-content) max-content',
                            justifyContent: 'start',
                            minWidth: 0,
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            mt: 0.5,
                        }}>
                            <Box component="span" sx={{
                                display: 'block',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {owner.name}
                            </Box>
                            <Box component="span" sx={{
                                display: 'block',
                                whiteSpace: 'nowrap',
                            }}>
                                {owner.ic ? `\u00A0· ${t('checkout.orderSummary.identificationNumber')}: ${owner.ic}` : null}
                                {' · '}
                                {t(owner.vat_payer
                                    ? 'checkout.orderSummary.vatPayer'
                                    : 'checkout.orderSummary.notVatPayer')}
                            </Box>
                        </Typography>
                    ) : null}
                </Box>
                <Box sx={{
                    display: 'flex',
                    gap: 1.5,
                    pt: minimumStay ? 1.5 : 0,
                }}>
                    <Box>
                        <b>{beginsAtDateLabel}</b>, {beginsAtTimeLabel}
                    </Box>
                    <ArrowForwardIcon color="#BDBDBD" width={20} height={20} />
                    <CheckoutEndTime endsAtDateLabel={endsAtDateLabel} endsAtTimeLabel={endsAtTimeLabel} minimumStay={minimumStay} />
                </Box>
                <Box display="flex" justifyContent="space-between">
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                        <ReservationDuration
                            reservationDays={reservationDays}
                            reservationHours={reservationHours}
                        />
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 0.25,
                    }}>
                        {hasDiscount ? (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 1,
                            }}>
                                <span>{t('checkout.orderSummary.originalPrice')}</span>
                                <span style={{ textDecoration: 'line-through' }}>
                                    {formatCzkAmount(originalPrice, locale)}
                                </span>
                            </Box>
                        ) : null}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1,
                        }}>
                            <span>{t('checkout.orderSummary.total')}</span>
                            <Box component="span" sx={{
                                color: 'customGreen.main',
                                whiteSpace: 'nowrap',
                            }}>
                                <b>{formatCzkAmount(finalPrice, locale)}</b>
                                {owner?.vat_payer ? ` ${t('checkout.orderSummary.vatIncluded')}` : null}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
